/**
 * Marks the text the reader's place can be held against; the value identifies it
 * across re-renders. Transcripts mark each entry rather than each turn, since a
 * rename can merge or split turns but never changes an entry.
 */
export const READING_ANCHOR_ATTRIBUTE = 'data-reading-anchor'

interface ReadingAnchor {
  element: Element
  id: string
  /**
   * Where the anchored text sat in the viewport. Viewport-relative rather than
   * content-relative: a regroup can shrink the content enough that the browser
   * clamps scrollTop before the hold runs, and a content offset would take that
   * clamp for the reader scrolling.
   */
  viewportTop: number
  /** scrollTop right after the last hold, so the scroll event it fires isn't taken for the reader's. */
  heldScrollTop: number | null
}

const anchors = new WeakMap<HTMLElement, ReadingAnchor>()

function viewportTopOf(element: Element, scroller: HTMLElement): number {
  return element.getBoundingClientRect().top - scroller.getBoundingClientRect().top
}

function findAnchorElement(scroller: HTMLElement, id: string): Element | undefined {
  return Array.from(scroller.querySelectorAll(`[${READING_ANCHOR_ATTRIBUTE}]`)).find(
    (candidate) => candidate.getAttribute(READING_ANCHOR_ATTRIBUTE) === id,
  )
}

/** Remembers the topmost text still visible as the reader's place. */
export function recordReadingAnchor(scroller: HTMLElement): void {
  const viewportTop = scroller.getBoundingClientRect().top
  let anchor: Element | null = null
  let anchorTop = Infinity
  for (const element of scroller.querySelectorAll(`[${READING_ANCHOR_ATTRIBUTE}]`)) {
    const rect = element.getBoundingClientRect()
    if (rect.bottom <= viewportTop || rect.top >= anchorTop) continue
    anchor = element
    anchorTop = rect.top
  }
  if (!anchor) {
    anchors.delete(scroller)
    return
  }
  anchors.set(scroller, {
    element: anchor,
    id: anchor.getAttribute(READING_ANCHOR_ATTRIBUTE) ?? '',
    viewportTop: anchorTop - viewportTop,
    heldScrollTop: null,
  })
}

export function clearReadingAnchor(scroller: HTMLElement): void {
  anchors.delete(scroller)
}

/** The id of the text holding the reader's place, so a virtualized list can keep it mounted. */
export function readingAnchorId(scroller: HTMLElement | null | undefined): string | undefined {
  return scroller ? anchors.get(scroller)?.id : undefined
}

/** Re-records the reader's place after a scroll, unless the scroll was a hold's own. */
export function noteReaderScroll(scroller: HTMLElement): void {
  if (anchors.get(scroller)?.heldScrollTop === scroller.scrollTop) return
  recordReadingAnchor(scroller)
}

/**
 * Scrolls so the anchored text sits where it was in the viewport, whatever grew,
 * shrank or regrouped above it. The text is found again by id when a re-key
 * remounted it; text that's gone entirely is skipped rather than guessed at, and
 * the place is re-recorded from what's on screen.
 */
export function holdReadingAnchor(scroller: HTMLElement): void {
  const anchor = anchors.get(scroller)
  const element = anchor && (anchor.element.isConnected ? anchor.element : findAnchorElement(scroller, anchor.id))
  if (!anchor || !element) {
    recordReadingAnchor(scroller)
    return
  }
  const shift = viewportTopOf(element, scroller) - anchor.viewportTop
  if (shift !== 0) scroller.scrollTop += shift
  // Kept rather than re-recorded: the content may still be settling (rows
  // measuring over their estimates), and the next hold has to aim for the same place.
  anchors.set(scroller, { ...anchor, element, heldScrollTop: scroller.scrollTop })
}
